import React, { Component } from 'react';
import {Link} from 'react-router-dom';
import HttpRunner from '../query';
import DetectLanguageQuery from '../detectLanguageQuery';
import urlHandler from '../../../utils/urlHandler';
import perms from '../../../utils/permissions';
import examples from '../../../utils/examples';
import miscUtils from '../../../utils/miscUtils';

class DocV2DetectLanguage extends Component {
    constructor(props){
        super(props);

        this.state = {
            dynamicSample: false
        }
    }

    componentDidMount() {
        this.setState({
            dynamicSample: perms.checkEndpoint(urlHandler.API_ENDPOINT_V2_DETECT_LANGUAGE)
        })
    }

    render() {        
        return (
            <div className="bucket" id="top">
                <h3 className="heading-1">Detect Language</h3>
                <code className="api-definition">
                    <span className="post-badge">POST</span>&nbsp;{urlHandler.API_ENDPOINT_V2_DETECT_LANGUAGE}
                </code>
                <div className="description">
                    The detect-language endpoint detects the predominant language in a piece of text. Multiple results may 
                    be returned if the text is not clearly a single language. In that case an array of possible languages 
                    is returned which includes the confidence that each is the predominant language.<br/>
                    <br/> 
                    NOTE: See <Link to="/documentation/doc_v2detectlanguages">detect-languages</Link> endpoint for a more robust 
                    detection of multiple languages.
                    <br/>
                </div>

                <h4 className="heading-2" id="detectlanguagev2_request">Request</h4>
                <div className="description">
                    The detect-language request is a JSON object that contains the request parameters.<br/>
                </div>

                <h4 className="heading-3" id="detectlanguagev2_request_object">Request object</h4>

                <table className="table-wrapper">
                    <thead className="table-header">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">requestId</td>
                        <td className="meaning">A system generated request identifier.
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">documentId</td>
                        <td className="meaning">A client supplied tracking identifier.
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">text</td>
                        <td className="meaning">The UTF-8 block of text to be analyzed for languages.
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Required    
                        </td>
                    </tr>
                    <tr>
                        <td className="attribute">threshold</td>
                        <td className="meaning">Confidence threshold for a language to be returned.
                        </td>
                        <td className="type">Float</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">languageCandidates</td>
                        <td className="meaning">List of ISO-639-1 and/or ISO-639-3 codes for languages to be retuned; "*" will use all models.
                        </td>
                        <td className="type">String[]</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-2" id="detectlanguagev2_response">Response</h4>
                <div className="description">
                    The detect-language response object contains an array of languages and confidence values 
                    found in the request text object.
                </div>

                <h4 className="heading-3" id="detectlanguagev2_response_object">Response object</h4>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">languages</td>
                        <td className="meaning">Array of language information detected in the text.
                        </td>
                        <td className="type">An array of <a href="#detectlanguagev2_lang_info">language information</a> objects.</td>
                        <td className="type"></td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="detectlanguagev2_lang_info">Language information object</h4>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">language</td>
                        <td className="meaning">Name of the language.
                        </td>
                        <td className="type">String</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">iso639_1</td>
                        <td className="meaning">Two character language code.
                        </td>
                        <td className="type">String</td>
                        <td className="type">See {miscUtils.createExternalLink('https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes','ISO 639-1/3 codes')} for codes.</td>
                    </tr>
                    <tr>
                        <td className="attribute">iso639_3</td>
                        <td className="meaning">Three character language code.
                        </td>
                        <td className="type">String</td>
                        <td className="type">See {miscUtils.createExternalLink('https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes','ISO 639-1/3 codes')} for codes.</td>
                    </tr>
                    <tr>
                        <td className="attribute">confidence</td>
                        <td className="meaning">The confidence that this is the predominant language.
                        </td>
                        <td className="type">Float</td>
                        <td className="type"></td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-2" id="detectlanguagev2_mixed">Example</h4>
                <div className="example-content host">
                    <pre>
                        POST {urlHandler.API_ENDPOINT_V2_DETECT_LANGUAGE}<br/>
                        Accept: application/json<br/>
                        Authorization: Bearer [YOUR-API-KEY]<br/>
                        Content-Type: application/json<br/>
                        <br/>
                    </pre>
                    <pre>{JSON.stringify(examples.EXAMPLE_REQUEST_DETECT_LANGUAGE2_1, null, 3)}</pre>
                </div>
                <HttpRunner example="detectlanguage1" 
                    query="detect-language2"
                    response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_DETECT_LANGUAGE2_1) : null}
                />
            </div>
        );
    }
}

export default DocV2DetectLanguage;